import { useEffect } from 'react';
import { C } from '../constants';
import { setMeta } from '../utils/meta';
import Glass from '../components/Glass';
import { Calendar } from 'lucide-react';

const accent = C.cyan;

const STATES_2026 = [
  { state: 'Tamil Nadu',   seats: 234, voted: 'April 2026', note: 'TVK\'s debut alongside DMK and AIADMK', live: true },
  { state: 'West Bengal',  seats: 294, voted: 'April 2026', note: 'TMC vs BJP with Left-Congress alliance', live: true },
  { state: 'Kerala',       seats: 140, voted: 'April 2026', note: 'LDF vs UDF, BJP expanding south' },
  { state: 'Assam',        seats: 126, voted: 'April 2026', note: 'BJP seeking third consecutive term' },
  { state: 'Puducherry',   seats: 30,  voted: 'April 2026', note: 'AINRC-led alliance vs Congress' },
];

const STATES_2027_EARLY = [
  { state: 'Uttar Pradesh', seats: 403, districts: 75, timing: 'Feb–Mar 2027', context: 'India\'s largest state by population. The result shapes the Lok Sabha narrative.' },
  { state: 'Punjab',        seats: 117, districts: 23, timing: 'Feb–Mar 2027', context: 'AAP\'s first re-election test after forming government in 2022.' },
  { state: 'Goa',           seats: 40,  districts: 2,  timing: 'Feb–Mar 2027', context: 'Small but politically significant with multi-party contest.' },
  { state: 'Uttarakhand',   seats: 70,  districts: 13, timing: 'Feb–Mar 2027', context: 'Hill state with a tradition of anti-incumbency alternation.' },
  { state: 'Manipur',       seats: 60,  districts: 16, timing: 'Feb–Mar 2027', context: 'Politically complex state shaped by ethnic dynamics.' },
];

const STATES_2027_LATE = [
  { state: 'Himachal Pradesh', seats: 68,  districts: 12, timing: 'Nov 2027',  context: 'Strong tradition of alternating between parties.' },
  { state: 'Gujarat',          seats: 182, districts: 33, timing: 'Dec 2027',  context: 'Governed by the same party continuously since 1998.' },
];

function SectionLabel({ children }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase',
      color: accent, fontWeight: 700, marginBottom: '0.6rem',
    }}>
      <Calendar size={12} color={accent} />
      {children}
    </div>
  );
}

function StateTable2026({ rows }) {
  return (
    <Glass style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: `1px solid ${C.border}` }}>
              {['State', 'Seats', 'Voted', 'Key Storyline', 'On Platform'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: C.textDim, fontWeight: 600, fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} style={{ borderTop: i > 0 ? `1px solid ${C.border}` : 'none' }}>
                <td style={{ padding: '10px 14px', color: C.white, fontWeight: 600, whiteSpace: 'nowrap' }}>{row.state}</td>
                <td style={{ padding: '10px 14px', color: C.textDim }}>{row.seats}</td>
                <td style={{ padding: '10px 14px', color: C.textDim, whiteSpace: 'nowrap' }}>{row.voted}</td>
                <td style={{ padding: '10px 14px', color: 'rgba(255,255,255,0.65)', fontSize: '0.82rem' }}>{row.note}</td>
                <td style={{ padding: '10px 14px' }}>
                  {row.live
                    ? <span style={{ color: '#4CAF50', fontWeight: 700, fontSize: '0.78rem', background: 'rgba(76,175,80,0.1)', padding: '2px 8px', borderRadius: 999 }}>Live</span>
                    : <span style={{ color: C.textDim, fontSize: '0.78rem' }}>—</span>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Glass>
  );
}

function StateTable2027({ rows }) {
  return (
    <Glass style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: `1px solid ${C.border}` }}>
              {['State', 'Seats', 'Districts', 'Timing', 'Context'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: C.textDim, fontWeight: 600, fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} style={{ borderTop: i > 0 ? `1px solid ${C.border}` : 'none' }}>
                <td style={{ padding: '10px 14px', color: C.white, fontWeight: 600, whiteSpace: 'nowrap' }}>{row.state}</td>
                <td style={{ padding: '10px 14px', color: C.textDim }}>{row.seats}</td>
                <td style={{ padding: '10px 14px', color: C.textDim }}>{row.districts}</td>
                <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                  <span style={{ color: accent, fontWeight: 600, fontSize: '0.78rem', background: 'rgba(0,229,255,0.08)', padding: '2px 8px', borderRadius: 999 }}>
                    {row.timing}
                  </span>
                </td>
                <td style={{ padding: '10px 14px', color: 'rgba(255,255,255,0.65)', fontSize: '0.82rem' }}>{row.context}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Glass>
  );
}

export default function ElectionCalendarScreen() {
  useEffect(() => {
    setMeta({
      title:       'India Election Calendar 2026-2029 - Every State Election Date | UnofficialElections',
      description: 'Complete guide to every Indian state assembly election from 2026 to 2029, including dates, seats, and districts. Track them all on UnofficialElections.',
      ogTitle:     'India Election Calendar 2026–2029 - Every State Election You Need to Know',
      ogImage:     '/og-home.png',
      canonical:   '/election-calendar',
    });
  }, []);
  return (
    <main className="px-5 md:px-10 py-10 max-w-4xl mx-auto">
      <div style={{ fontSize: '0.7rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: accent, fontWeight: 600 }}>
        Election Calendar
      </div>
      <h1 style={{
        fontWeight: 700, fontSize: 'clamp(1.8rem, 4.5vw, 3rem)',
        lineHeight: 1.05, letterSpacing: '-0.025em', marginTop: '0.4rem', color: C.white,
      }}>
        India Election Calendar
        <br />
        <span style={{ color: C.textDim, fontWeight: 400, fontSize: '0.6em' }}>2026 – 2029</span>
      </h1>
      <p style={{ fontSize: '0.95rem', color: C.textDim, marginTop: '0.6rem', maxWidth: 620, lineHeight: 1.65 }}>
        Between now and the 2029 Lok Sabha general elections, nearly every Indian state will go to the polls at least once. This is your complete guide to every upcoming state assembly election — dates, seats, and how UnofficialElections will cover each one.
      </p>

      {/* 2026 */}
      <div style={{ marginTop: '2.5rem' }}>
        <SectionLabel>2026 — States Already Voted</SectionLabel>
        <h2 style={{ fontWeight: 700, fontSize: '1.2rem', color: C.white, marginBottom: '0.5rem' }}>
          Which States Voted in 2026?
        </h2>
        <p style={{ fontSize: '0.9rem', color: C.textDim, marginBottom: '1rem', lineHeight: 1.6 }}>
          Five states and one union territory voted in 2026, shaping the political picture ahead of 2029. UnofficialElections tracked district-level data for Tamil Nadu and West Bengal in real time.
        </p>
        <StateTable2026 rows={STATES_2026} />
      </div>

      {/* 2027 early */}
      <div style={{ marginTop: '3rem' }}>
        <SectionLabel>Early 2027 — February–March</SectionLabel>
        <h2 style={{ fontWeight: 700, fontSize: '1.2rem', color: C.white, marginBottom: '0.5rem' }}>
          Which States Vote in Early 2027?
        </h2>
        <p style={{ fontSize: '0.9rem', color: C.textDim, marginBottom: '1rem', lineHeight: 1.6 }}>
          2027 is a major election year. Five assemblies go to the polls in the first quarter, including India's most populous state, Uttar Pradesh — essentially a national election in miniature.
        </p>
        <StateTable2027 rows={STATES_2027_EARLY} />
      </div>

      {/* 2027 late */}
      <div style={{ marginTop: '2.5rem' }}>
        <SectionLabel>Late 2027 — November–December</SectionLabel>
        <h2 style={{ fontWeight: 700, fontSize: '1.2rem', color: C.white, marginBottom: '0.5rem' }}>
          Which States Vote in Late 2027?
        </h2>
        <p style={{ fontSize: '0.9rem', color: C.textDim, marginBottom: '1rem', lineHeight: 1.6 }}>
          Two more states vote in the second half of 2027 — one with a strong alternation pattern, the other with a long unbroken record of single-party governance.
        </p>
        <StateTable2027 rows={STATES_2027_LATE} />
      </div>

      {/* 2028-2029 */}
      <div style={{ marginTop: '3rem' }}>
        <SectionLabel>2028 – 2029</SectionLabel>
        <h2 style={{ fontWeight: 700, fontSize: '1.2rem', color: C.white, marginBottom: '0.5rem' }}>
          What Happens in 2028 and 2029?
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Glass style={{ padding: '1.1rem 1.3rem' }}>
            <div style={{ fontWeight: 700, fontSize: '0.95rem', color: C.white, marginBottom: '0.35rem' }}>2028 — Critical State Elections</div>
            <p style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.7, margin: 0 }}>
              Madhya Pradesh, Rajasthan, Chhattisgarh, and Telangana all go to the polls, each a significant political contest in its own right. The platform will add each state as its election approaches with full district maps.
            </p>
          </Glass>
          <Glass style={{ padding: '1.1rem 1.3rem', border: `1px solid ${accent}33` }}>
            <div style={{ fontWeight: 700, fontSize: '0.95rem', color: accent, marginBottom: '0.35rem' }}>2029 — Lok Sabha General Elections</div>
            <p style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.7, margin: 0 }}>
              The main event: the Lok Sabha general elections that determine India's next government. The long-term target is constituency-level coverage across all of India. Every state election between now and then is a building block toward that contest.
            </p>
          </Glass>
        </div>
      </div>

      {/* How coverage works */}
      <div style={{ marginTop: '3rem' }}>
        <h2 style={{ fontWeight: 700, fontSize: '1.1rem', color: C.white, marginBottom: '0.5rem' }}>
          How Will UnofficialElections Cover These Elections?
        </h2>
        <p style={{ fontSize: '0.92rem', color: 'rgba(255,255,255,0.72)', lineHeight: 1.75, margin: 0 }}>
          Each state will be added to the platform as its election approaches, with full district maps, all contesting parties, and real-time results. The platform is building toward having every state available — featuring the full district tile map, all contesting parties, real-time result updates, and district-level breakdowns. The goal is to have the infrastructure in place for constituency-level coverage for the 2029 Lok Sabha elections.
        </p>
      </div>

      <Glass style={{ marginTop: '2.5rem', padding: '1rem 1.3rem' }}>
        <p style={{ fontSize: '0.85rem', color: C.textDim, margin: 0, lineHeight: 1.6 }}>
          UnofficialElections.com — tracking how India votes, one state at a time. Unofficial, non-political, entertainment only.
        </p>
      </Glass>
    </main>
  );
}
